'use client';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import type { CampaignFormData } from './CampaignBuilderClient';

interface Props { form: UseFormReturn<CampaignFormData>; }

const contentTypes = [
  { id: 'ct-reels', key: 'contentReels' as const, label: 'Reels Instagram', desc: 'Courtes vidéos verticales 15-60s', icon: '🎬', platform: 'Instagram' },
  { id: 'ct-stories', key: 'contentStories' as const, label: 'Stories Instagram', desc: 'Contenu éphémère 24h', icon: '📸', platform: 'Instagram' },
  { id: 'ct-tiktok', key: 'contentTiktok' as const, label: 'TikTok', desc: 'Vidéos TikTok natives', icon: '🎵', platform: 'TikTok' },
  { id: 'ct-ugc', key: 'contentUGC' as const, label: 'UGC (réutilisable)', desc: 'Contenus authentiques pour vos propres canaux', icon: '📱', platform: 'Multi' },
];

const platforms = ['instagram', 'tiktok', 'youtube', 'snapchat'];
const platformLabels: Record<string, string> = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', snapchat: 'Snapchat' };
const timelines = ['1 semaine', '2 semaines', '3 semaines', '1 mois', '6 semaines', '2 mois', '3 mois'];

export default function StepContentMix({ form }: Props) {
  const { register, watch, setValue } = form;
  const selectedPlatforms = watch('platforms');

  const togglePlatform = (p: string) => {
    const arr = watch('platforms');
    setValue('platforms', arr.includes(p) ? arr.filter(x => x !== p) : [...arr, p]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Mix de contenus</h2>
        <p className="text-sm text-muted-foreground">Définissez le volume et le type de contenus attendus.</p>
      </div>

      {/* Platforms */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Plateformes de diffusion</label>
        <div className="flex flex-wrap gap-3">
          {platforms.map(p => (
            <button
              key={`cp-${p}`}
              type="button"
              onClick={() => togglePlatform(p)}
              className={`px-4 py-2 rounded-lg text-sm font-600 transition-all ${
                selectedPlatforms.includes(p)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {platformLabels[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Content volumes */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-3">Volume de contenus</label>
        <div className="space-y-4">
          {contentTypes.map((ct) => (
            <div key={ct.id} className="flex items-center justify-between bg-muted/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{ct.icon}</span>
                <div>
                  <p className="text-sm font-700 text-foreground">{ct.label}</p>
                  <p className="text-xs text-muted-foreground">{ct.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setValue(ct.key, Math.max(0, watch(ct.key) - 1))}
                  className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all"
                >
                  −
                </button>
                <span className="w-8 text-center text-sm font-700 text-foreground font-mono">
                  {watch(ct.key)}
                </span>
                <button
                  type="button"
                  onClick={() => setValue(ct.key, watch(ct.key) + 1)}
                  className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Durée de la campagne</label>
        <div className="flex flex-wrap gap-2">
          {timelines.map(t => (
            <button
              key={`tl-${t}`}
              type="button"
              onClick={() => setValue('timeline', t)}
              className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all ${
                watch('timeline') === t
                  ? 'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Certified only */}
      <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl p-4">
        <div>
          <p className="text-sm font-700 text-foreground">Créateurs certifiés uniquement</p>
          <p className="text-xs text-muted-foreground mt-0.5">Filtrer uniquement les créateurs vérifiés par 360</p>
        </div>
        <button
          type="button"
          onClick={() => setValue('certifiedOnly', !watch('certifiedOnly'))}
          className={`w-12 h-6 rounded-full transition-all duration-300 relative ${
            watch('certifiedOnly') ? 'bg-primary' : 'bg-border'
          }`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
            watch('certifiedOnly') ? 'left-6' : 'left-0.5'
          }`} />
        </button>
      </div>
    </div>
  );
}